import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Stage,
  Layer,
  Line,
  Rect,
  Circle,
  Arrow,
  Text as KonvaText,
  Image as KonvaImage,
  Transformer,
} from "react-konva";
import Konva from "konva";
import { Stroke } from "./types/chart";
import styles from "./ChartCanvas.module.css";

/**
 * `crossOrigin="anonymous"` forces a CORS-enabled load; CloudFront/S3 signed URLs
 * often omit CORS for the admin origin, which makes `Image` fail entirely. Only
 * set it for same-origin URLs (and rely on omit for typical PHI document URLs).
 */
function shouldSetAnonymousCrossOrigin(imageUrl: string): boolean {
  if (
    !imageUrl ||
    imageUrl.startsWith("data:") ||
    imageUrl.startsWith("blob:")
  ) {
    return false;
  }
  try {
    const u = new URL(imageUrl, window.location.href);
    return u.origin === window.location.origin;
  } catch {
    return false;
  }
}

/**
 * Cross-origin `img.src = https://…` loads for display but taints the canvas, so
 * `toBlob` / `toDataURL` fail. Prefer `fetch` + blob URL when CORS allows reading
 * the bytes (common for signed asset URLs that send `Access-Control-Allow-Origin`).
 */
function imageUrlShouldTryFetchBlobFirst(imageUrl: string): boolean {
  if (
    !imageUrl ||
    imageUrl.startsWith("data:") ||
    imageUrl.startsWith("blob:")
  ) {
    return false;
  }
  try {
    const u = new URL(imageUrl, window.location.href);
    if (u.protocol !== "http:" && u.protocol !== "https:") return false;
    return u.origin !== window.location.origin;
  } catch {
    return false;
  }
}

/** Pending text input state passed from the editor */
export interface TextInputState {
  /** Stable key so the DOM input remounts for each placement */
  id: string;
  /** Position in image-space (intrinsic pixel coords) */
  x: number;
  y: number;
  color: string;
  fontSize: number;
}

interface ChartCanvasProps {
  baseImageUrl: string;
  strokes: Stroke[];
  currentStroke: Stroke | null;
  onMouseDown: (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => void;
  onMouseMove: (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => void;
  onMouseUp: () => void;
  toolCursor?: string;
  /** When set, shows an inline text input at this position */
  textInput?: TextInputState | null;
  onTextSubmit?: (text: string, placement: TextInputState) => void;
  onTextCancel?: () => void;
  /** Whether base image failed to load */
  imageError?: boolean;
  onRetryImage?: () => void;
  /** ID of the currently selected stroke (for select tool) */
  selectedStrokeId?: string | null;
  /** Called when a selected stroke is dragged or transformed */
  onStrokeTransform?: (strokeId: string, updates: Partial<Stroke>) => void;
}

export interface ChartCanvasRef {
  getStage: () => Konva.Stage | null;
  /**
   * Commits any typed-but-uncommitted text in the active inline input as a
   * text stroke. Returns true if something was committed (caller should NOT
   * then open a new input, since the act of committing dismisses the old
   * one). Use this BEFORE replacing the active textInput state — React tears
   * the old <input> off the DOM without reliably firing onBlur, so without
   * this call the typed text is silently dropped.
   */
  flushPendingText: () => boolean;
}

interface StrokeHandlers {
  onDragEnd?: (e: Konva.KonvaEventObject<DragEvent>) => void;
}

function renderStroke(stroke: Stroke, handlers: StrokeHandlers = {}) {
  const key = stroke.id;
  const commonAttrs = {
    "data-stroke-id": stroke.id,
    onDragEnd: handlers.onDragEnd,
  };

  switch (stroke.tool) {
    case "pen":
    case "highlighter":
      return (
        <Line
          key={key}
          points={stroke.points}
          stroke={stroke.color}
          strokeWidth={stroke.width}
          opacity={stroke.opacity}
          lineCap="round"
          lineJoin="round"
          tension={0.3}
          {...commonAttrs}
        />
      );

    case "rectangle": {
      const x = Math.min(stroke.x ?? 0, stroke.endX ?? 0);
      const y = Math.min(stroke.y ?? 0, stroke.endY ?? 0);
      const w = Math.abs((stroke.endX ?? 0) - (stroke.x ?? 0));
      const h = Math.abs((stroke.endY ?? 0) - (stroke.y ?? 0));
      return (
        <Rect
          key={key}
          x={x}
          y={y}
          width={w}
          height={h}
          stroke={stroke.color}
          strokeWidth={stroke.width}
          opacity={stroke.opacity}
          rotation={stroke.rotation ?? 0}
          {...commonAttrs}
        />
      );
    }

    case "circle":
      return (
        <Circle
          key={key}
          x={stroke.x ?? 0}
          y={stroke.y ?? 0}
          radius={stroke.radius ?? 0}
          stroke={stroke.color}
          strokeWidth={stroke.width}
          opacity={stroke.opacity}
          rotation={stroke.rotation ?? 0}
          {...commonAttrs}
        />
      );

    case "arrow":
      return (
        <Arrow
          key={key}
          points={stroke.points}
          stroke={stroke.color}
          strokeWidth={stroke.width}
          opacity={stroke.opacity}
          fill={stroke.color}
          pointerLength={stroke.width * 3}
          pointerWidth={stroke.width * 3}
          {...commonAttrs}
        />
      );

    case "text":
      return (
        <KonvaText
          key={key}
          x={stroke.x ?? 0}
          y={stroke.y ?? 0}
          text={stroke.text ?? ""}
          fontSize={stroke.fontSize ?? 20}
          fill={stroke.color}
          opacity={stroke.opacity}
          rotation={stroke.rotation ?? 0}
          {...commonAttrs}
        />
      );

    case "emoji":
      return (
        <KonvaText
          key={key}
          x={stroke.x ?? 0}
          y={stroke.y ?? 0}
          text={stroke.text ?? ""}
          fontSize={stroke.fontSize ?? 32}
          opacity={stroke.opacity}
          rotation={stroke.rotation ?? 0}
          {...commonAttrs}
        />
      );

    default:
      return null;
  }
}

const ChartCanvas = forwardRef<ChartCanvasRef, ChartCanvasProps>(
  (
    {
      baseImageUrl,
      strokes,
      currentStroke,
      onMouseDown,
      onMouseMove,
      onMouseUp,
      toolCursor = "crosshair",
      textInput,
      onTextSubmit,
      onTextCancel,
      imageError,
      onRetryImage,
      selectedStrokeId,
      onStrokeTransform,
    },
    ref,
  ) => {
    const stageRef = useRef<Konva.Stage>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const textInputRef = useRef<HTMLInputElement>(null);
    const [image, setImage] = useState<HTMLImageElement | null>(null);
    const [stageSize, setStageSize] = useState({ width: 800, height: 600 });
    const [imgLoadError, setImgLoadError] = useState(false);
    const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });

    const fitStageToContainer = useCallback(
      (img: HTMLImageElement, el: HTMLDivElement) => {
        const cw = el.clientWidth;
        const ch = el.clientHeight;
        if (cw <= 0) return;
        const scale =
          ch <= 0 ? cw / img.width : Math.min(cw / img.width, ch / img.height);
        setStageSize({
          width: img.width * scale,
          height: img.height * scale,
        });
      },
      [],
    );
    const isPanning = useRef(false);
    const lastPanPos = useRef({ x: 0, y: 0 });
    const spaceHeld = useRef(false);
    /**
     * Refs above are read synchronously inside Konva mouse handlers, so we
     * cannot derive them from state. To keep the container cursor in sync
     * with Space/pan transitions we mirror them into state values that
     * actually trigger re-renders.
     */
    const [isSpaceHeldUi, setIsSpaceHeldUi] = useState(false);
    const [isPanningUi, setIsPanningUi] = useState(false);
    const transformerRef = useRef<Konva.Transformer>(null);
    const annotationLayerRef = useRef<Konva.Layer>(null);

    useImperativeHandle(
      ref,
      () => ({
        getStage: () => stageRef.current,
        flushPendingText: () => {
          const val = inputValueRef.current.trim();
          const placement = inputPlacementRef.current;
          if (val && placement && onTextSubmit) {
            // Reset locals BEFORE invoking onTextSubmit so the impending blur
            // (from React unmounting the old <input>) can't double-commit
            // via the DOM read fallback in readCurrentTextValue.
            inputValueRef.current = "";
            inputPlacementRef.current = null;
            onTextSubmit(val, placement);
            return true;
          }
          return false;
        },
      }),
      [onTextSubmit],
    );

    // Tracks the TextInputState that the currently mounted <input> corresponds to.
    // Needed because blur fires during React commit (old input unmounts) when
    // the textInput prop has already been updated to the NEW placement.
    const inputPlacementRef = useRef<TextInputState | null>(null);
    const textInputCreatedAt = useRef(0);
    const pendingCancelTimer = useRef<ReturnType<typeof setTimeout> | null>(
      null,
    );
    /**
     * Mirrors the live <input> value, updated on every onInput event. On
     * tablet/mobile the DOM read at blur time can race with focus transitions
     * (and on some platforms with IME flush) and return an empty string even
     * after the user typed. Reading from this ref is reliable because it's
     * captured synchronously on each keystroke.
     */
    const inputValueRef = useRef("");

    // Focus text input when it appears
    useEffect(() => {
      if (textInput) {
        // Cancel any deferred cancel from a previous blur (replacement scenario)
        if (pendingCancelTimer.current) {
          clearTimeout(pendingCancelTimer.current);
          pendingCancelTimer.current = null;
        }
        inputPlacementRef.current = textInput;
        inputValueRef.current = "";
        textInputCreatedAt.current = Date.now();
        const frameId = requestAnimationFrame(() => {
          textInputRef.current?.focus();
        });
        return () => cancelAnimationFrame(frameId);
      } else {
        inputPlacementRef.current = null;
        inputValueRef.current = "";
      }
    }, [textInput]);

    const handleTransformEndRef = useRef<typeof handleTransformEnd | null>(
      null,
    );

    /**
     * Track the selected Konva node in state so the Transformer's `nodes`
     * prop can be React-controlled. Imperatively calling tr.nodes([...])
     * was unreliable because react-konva appears to reset the Transformer's
     * nodes whenever it reconciles props, leaving us with an empty
     * Transformer between renders.
     */
    const [selectedNode, setSelectedNode] = useState<Konva.Shape | null>(null);

    useEffect(() => {
      const layer = annotationLayerRef.current;
      if (!layer) return;
      if (!selectedStrokeId) {
        setSelectedNode((prev) => (prev === null ? prev : null));
        return;
      }
      const node = (layer.findOne(
        (n: Konva.Node) => n.getAttr("data-stroke-id") === selectedStrokeId,
      ) ?? null) as Konva.Shape | null;
      // Object.is comparison via setState bail-out keeps this stable when
      // findOne returns the same node across re-runs.
      setSelectedNode((prev) => (prev === node ? prev : node));
    }, [selectedStrokeId, strokes]);

    useEffect(() => {
      if (!selectedNode) return;
      selectedNode.draggable(true);
      const transformEndListener = (e: Konva.KonvaEventObject<Event>) => {
        handleTransformEndRef.current?.(e);
      };
      selectedNode.on("transformend.chartcanvas", transformEndListener);
      return () => {
        selectedNode.off("transformend.chartcanvas");
        selectedNode.draggable(false);
      };
    }, [selectedNode]);

    const transformerNodes = useMemo(
      () => (selectedNode ? [selectedNode] : []),
      [selectedNode],
    );

    /**
     * Stable callbacks for Transformer props — passing fresh function
     * references on every render appears to cause react-konva to reset
     * the Transformer's internal state.
     */
    const transformerBoundBoxFunc = useCallback(
      (
        oldBox: {
          x: number;
          y: number;
          width: number;
          height: number;
          rotation: number;
        },
        newBox: {
          x: number;
          y: number;
          width: number;
          height: number;
          rotation: number;
        },
      ) => {
        if (Math.abs(newBox.width) < 5 || Math.abs(newBox.height) < 5) {
          return oldBox;
        }
        return newBox;
      },
      [],
    );

    // Space key tracking for pan mode
    useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.code === "Space" && !e.repeat) {
          spaceHeld.current = true;
          setIsSpaceHeldUi(true);
        }
      };
      const handleKeyUp = (e: KeyboardEvent) => {
        if (e.code === "Space") {
          spaceHeld.current = false;
          isPanning.current = false;
          setIsSpaceHeldUi(false);
          setIsPanningUi(false);
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      window.addEventListener("keyup", handleKeyUp);
      return () => {
        window.removeEventListener("keydown", handleKeyDown);
        window.removeEventListener("keyup", handleKeyUp);
      };
    }, []);

    // Load base image (cross-origin URLs: try fetch→blob first so export is not tainted)
    useEffect(() => {
      if (!baseImageUrl) {
        setImage(null);
        return;
      }
      setImgLoadError(false);
      let cancelled = false;
      let revokeBlob: string | null = null;

      const attachAndLoad = (src: string) => {
        const img = new window.Image();
        if (shouldSetAnonymousCrossOrigin(src)) {
          img.crossOrigin = "anonymous";
        }
        img.onload = () => {
          if (cancelled) {
            if (revokeBlob) URL.revokeObjectURL(revokeBlob);
            return;
          }
          setImage(img);
          setPanOffset({ x: 0, y: 0 });
          if (containerRef.current) {
            fitStageToContainer(img, containerRef.current);
          }
        };
        img.onerror = () => {
          if (revokeBlob) URL.revokeObjectURL(revokeBlob);
          revokeBlob = null;
          if (!cancelled) {
            setImgLoadError(true);
            setImage(null);
          }
        };
        img.src = src;
      };

      const run = async () => {
        if (!imageUrlShouldTryFetchBlobFirst(baseImageUrl)) {
          attachAndLoad(baseImageUrl);
          return;
        }
        try {
          const res = await fetch(baseImageUrl, {
            mode: "cors",
            credentials: "omit",
            cache: "no-store",
          });
          if (!res.ok) throw new Error(String(res.status));
          const blob = await res.blob();
          if (cancelled) return;
          const objectUrl = URL.createObjectURL(blob);
          if (cancelled) {
            URL.revokeObjectURL(objectUrl);
            return;
          }
          revokeBlob = objectUrl;
          attachAndLoad(objectUrl);
        } catch {
          if (cancelled) return;
          attachAndLoad(baseImageUrl);
        }
      };

      void run();

      return () => {
        cancelled = true;
        if (revokeBlob) URL.revokeObjectURL(revokeBlob);
      };
    }, [baseImageUrl, fitStageToContainer]);

    // Resize handler — contain entire image in width *and* height
    useEffect(() => {
      if (!image || !containerRef.current) return;
      const el = containerRef.current;
      const observer = new ResizeObserver(() => {
        if (!containerRef.current || !image) return;
        fitStageToContainer(image, containerRef.current);
      });
      observer.observe(el);
      return () => observer.disconnect();
    }, [image, fitStageToContainer]);

    const scale = image ? stageSize.width / image.width : 1;

    // Pan handlers (Space + drag)
    const handlePanStart = useCallback(
      (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
        if (!spaceHeld.current) return;
        isPanning.current = true;
        setIsPanningUi(true);
        const stage = e.target.getStage();
        const pos = stage?.getPointerPosition();
        if (pos) lastPanPos.current = pos;
      },
      [],
    );

    const handlePanMove = useCallback(
      (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
        if (!isPanning.current) return;
        const stage = e.target.getStage();
        const pos = stage?.getPointerPosition();
        if (!pos) return;
        const dx = pos.x - lastPanPos.current.x;
        const dy = pos.y - lastPanPos.current.y;
        lastPanPos.current = pos;
        setPanOffset((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
      },
      [],
    );

    const handlePanEnd = useCallback(() => {
      isPanning.current = false;
      setIsPanningUi(false);
    }, []);

    /**
     * Konva fires mousedown on a Transformer's anchor and the event bubbles
     * up to the Stage. Without this guard, the select-tool handler reads
     * the anchor as a non-stroke target and fires onDeselectStroke,
     * which clears the selection mid-transform — killing transform/
     * transformend before they can fire.
     */
    const isTransformerDescendant = (node: Konva.Node | null): boolean => {
      let current: Konva.Node | null = node;
      while (current) {
        if (current.getClassName?.() === "Transformer") return true;
        current = current.getParent();
      }
      return false;
    };

    const wrappedMouseDown = useCallback(
      (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
        if (spaceHeld.current) {
          handlePanStart(e);
          return;
        }
        if (isTransformerDescendant(e.target)) return;
        onMouseDown(e);
      },
      [onMouseDown, handlePanStart],
    );

    const wrappedMouseMove = useCallback(
      (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
        if (isPanning.current) {
          handlePanMove(e);
          return;
        }
        onMouseMove(e);
      },
      [onMouseMove, handlePanMove],
    );

    const wrappedMouseUp = useCallback(() => {
      if (isPanning.current) {
        handlePanEnd();
        return;
      }
      onMouseUp();
    }, [onMouseUp, handlePanEnd]);

    // Compute updated stroke fields after a drag or transform.
    // node.x()/y() return the absolute position after drag (original + delta),
    // NOT the delta alone. Line-based tools render at x=0 so it works out,
    // but position-based tools must subtract the rendered origin to get the delta.
    const handleDragEnd = useCallback(
      (e: Konva.KonvaEventObject<DragEvent>) => {
        const node = e.target as Konva.Shape;
        const strokeId = node.getAttr("data-stroke-id") as string | undefined;
        if (!strokeId || !onStrokeTransform) return;

        const stroke = strokes.find((s) => s.id === strokeId);
        if (!stroke) return;

        const nodeX = node.x();
        const nodeY = node.y();

        // Reset node position (we apply offset to stroke data instead)
        node.position({ x: 0, y: 0 });

        if (
          stroke.tool === "pen" ||
          stroke.tool === "highlighter" ||
          stroke.tool === "arrow"
        ) {
          // Line/Arrow nodes render at x=0, so nodeX IS the delta
          const newPoints = stroke.points.map((v, i) =>
            i % 2 === 0 ? v + nodeX : v + nodeY,
          );
          onStrokeTransform(strokeId, { points: newPoints });
        } else if (stroke.tool === "rectangle") {
          const renderX = Math.min(stroke.x ?? 0, stroke.endX ?? 0);
          const renderY = Math.min(stroke.y ?? 0, stroke.endY ?? 0);
          const deltaX = nodeX - renderX;
          const deltaY = nodeY - renderY;
          onStrokeTransform(strokeId, {
            x: (stroke.x ?? 0) + deltaX,
            y: (stroke.y ?? 0) + deltaY,
            endX: (stroke.endX ?? 0) + deltaX,
            endY: (stroke.endY ?? 0) + deltaY,
          });
        } else if (
          stroke.tool === "circle" ||
          stroke.tool === "text" ||
          stroke.tool === "emoji"
        ) {
          onStrokeTransform(strokeId, {
            x: nodeX,
            y: nodeY,
          });
        }
      },
      [strokes, onStrokeTransform],
    );

    const handleTransformEnd = useCallback(
      (e: Konva.KonvaEventObject<Event>) => {
        const node = e.target as Konva.Shape;
        const strokeId = node.getAttr("data-stroke-id") as string | undefined;
        if (!strokeId || !onStrokeTransform) return;

        const stroke = strokes.find((s) => s.id === strokeId);
        if (!stroke) return;

        const scaleX = node.scaleX();
        const scaleY = node.scaleY();
        const nodeX = node.x();
        const nodeY = node.y();
        const rotation = node.rotation();

        // Reset scale on the node (not controlled by React props)
        node.scaleX(1);
        node.scaleY(1);
        node.rotation(0);
        node.position({ x: 0, y: 0 });

        if (stroke.tool === "rectangle") {
          const renderX = Math.min(stroke.x ?? 0, stroke.endX ?? 0);
          const renderY = Math.min(stroke.y ?? 0, stroke.endY ?? 0);
          const deltaX = nodeX - renderX;
          const deltaY = nodeY - renderY;
          const origW = Math.abs((stroke.endX ?? 0) - (stroke.x ?? 0));
          const origH = Math.abs((stroke.endY ?? 0) - (stroke.y ?? 0));
          const newX = (stroke.x ?? 0) + deltaX;
          const newY = (stroke.y ?? 0) + deltaY;
          onStrokeTransform(strokeId, {
            x: newX,
            y: newY,
            endX: newX + origW * scaleX,
            endY: newY + origH * scaleY,
            rotation,
          });
        } else if (stroke.tool === "circle") {
          const avgScale = (Math.abs(scaleX) + Math.abs(scaleY)) / 2;
          onStrokeTransform(strokeId, {
            x: nodeX,
            y: nodeY,
            radius: (stroke.radius ?? 0) * avgScale,
            rotation,
          });
        } else if (stroke.tool === "text" || stroke.tool === "emoji") {
          const avgScale = (Math.abs(scaleX) + Math.abs(scaleY)) / 2;
          const defaultSize = stroke.tool === "emoji" ? 32 : 20;
          onStrokeTransform(strokeId, {
            x: nodeX,
            y: nodeY,
            fontSize: Math.round((stroke.fontSize ?? defaultSize) * avgScale),
            rotation,
          });
        } else if (
          stroke.tool === "pen" ||
          stroke.tool === "highlighter" ||
          stroke.tool === "arrow"
        ) {
          const xs = stroke.points.filter((_, i) => i % 2 === 0);
          const ys = stroke.points.filter((_, i) => i % 2 === 1);
          const minX = Math.min(...xs);
          const minY = Math.min(...ys);
          const newPoints = stroke.points.map((v, i) => {
            if (i % 2 === 0) return (v - minX) * scaleX + minX + nodeX;
            return (v - minY) * scaleY + minY + nodeY;
          });
          onStrokeTransform(strokeId, { points: newPoints, rotation });
        }
      },
      [strokes, onStrokeTransform],
    );

    // Keep the imperative listener in the attachment effect pointed at the latest handler
    handleTransformEndRef.current = handleTransformEnd;

    const handleTextInput = useCallback(
      (e: React.FormEvent<HTMLInputElement>) => {
        inputValueRef.current = e.currentTarget.value;
      },
      [],
    );

    /**
     * Read the current text value. Prefer the ref (captured via onInput on
     * every keystroke) over reading from the DOM target — on tablet/mobile
     * the DOM read can race with focus changes and return "" even when the
     * user has typed.
     */
    const readCurrentTextValue = (
      el?: HTMLInputElement | EventTarget | null,
    ): string => {
      if (inputValueRef.current) return inputValueRef.current;
      if (el && "value" in el) return (el as HTMLInputElement).value || "";
      return "";
    };

    const handleTextKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
          const val = readCurrentTextValue(e.target).trim();
          const placement = inputPlacementRef.current;
          if (val && onTextSubmit && placement) onTextSubmit(val, placement);
          else onTextCancel?.();
        } else if (e.key === "Escape") {
          onTextCancel?.();
        }
      },
      [onTextSubmit, onTextCancel],
    );

    const handleTextBlur = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        const valueAtBlur = readCurrentTextValue(e.target);
        const msSinceCreated = Date.now() - textInputCreatedAt.current;

        // Initial focus race: Konva may grab focus back from the just-mounted
        // <input>. Only re-focus when the user hasn't typed anything yet —
        // otherwise we'd discard typed text on tablet, where blur can fire
        // shortly after the user finishes typing.
        if (msSinceCreated < 300 && !valueAtBlur) {
          const el = e.target;
          requestAnimationFrame(() => el.focus());
          return;
        }

        const val = valueAtBlur.trim();
        const placement = inputPlacementRef.current;
        if (val && onTextSubmit && placement) {
          onTextSubmit(val, placement);
        } else {
          // Defer cancel so a replacement textInput (set in the same event
          // loop turn) can clear this timer before it fires.
          pendingCancelTimer.current = setTimeout(() => {
            pendingCancelTimer.current = null;
            onTextCancel?.();
          }, 60);
        }
      },
      [onTextSubmit, onTextCancel],
    );

    const showError = imageError || imgLoadError;

    if (showError) {
      return (
        <div ref={containerRef} className={styles.canvasContainer}>
          <div className={styles.errorState}>
            <p>Failed to load the base image.</p>
            <button className={styles.retryButton} onClick={onRetryImage}>
              Retry
            </button>
          </div>
        </div>
      );
    }

    return (
      <div
        ref={containerRef}
        className={styles.canvasContainer}
        style={{
          cursor: isSpaceHeldUi
            ? isPanningUi
              ? "grabbing"
              : "grab"
            : toolCursor,
        }}
      >
        <div className={styles.stageBackdrop}>
          <div
            className={styles.stageHost}
            style={{
              width: stageSize.width,
              height: stageSize.height,
            }}
          >
            <Stage
              ref={stageRef}
              width={stageSize.width}
              height={stageSize.height}
              scaleX={scale}
              scaleY={scale}
              x={panOffset.x}
              y={panOffset.y}
              onMouseDown={wrappedMouseDown}
              onMouseMove={wrappedMouseMove}
              onMouseUp={wrappedMouseUp}
              onTouchStart={wrappedMouseDown}
              onTouchMove={wrappedMouseMove}
              onTouchEnd={wrappedMouseUp}
            >
              {/* Layer 1: Base image */}
              <Layer listening={false}>
                {image && (
                  <KonvaImage
                    image={image}
                    width={image.width}
                    height={image.height}
                  />
                )}
              </Layer>

              {/* Layer 2: Hit surface + persisted annotations (hit rect catches empty/transparent areas for tools like Text). */}
              <Layer ref={annotationLayerRef}>
                {image ? (
                  <Rect
                    x={0}
                    y={0}
                    width={image.width}
                    height={image.height}
                    fill="rgba(0,0,0,0.001)"
                    listening
                  />
                ) : null}
                {strokes.map((s) =>
                  renderStroke(s, { onDragEnd: handleDragEnd }),
                )}
                <Transformer
                  ref={transformerRef}
                  nodes={transformerNodes}
                  boundBoxFunc={transformerBoundBoxFunc}
                  anchorSize={8}
                  borderStroke="var(--mantine-color-teal-5)"
                  anchorStroke="var(--mantine-color-teal-7)"
                  anchorFill="white"
                />
              </Layer>

              {/* Layer 3: Live preview of current stroke */}
              <Layer listening={false}>
                {currentStroke && renderStroke(currentStroke)}
              </Layer>
            </Stage>

            {/* Inline text input overlay (image space → screen via scale) */}
            {textInput && (
              <input
                key={textInput.id}
                ref={textInputRef}
                className={styles.textInput}
                style={{
                  left: panOffset.x + textInput.x * scale,
                  top: panOffset.y + textInput.y * scale,
                  fontSize: textInput.fontSize * scale,
                  color: textInput.color,
                }}
                defaultValue=""
                autoFocus
                autoComplete="off"
                onInput={handleTextInput}
                onKeyDown={handleTextKeyDown}
                onBlur={handleTextBlur}
                onMouseDown={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
              />
            )}
          </div>
        </div>
      </div>
    );
  },
);

ChartCanvas.displayName = "ChartCanvas";

export default ChartCanvas;
